/**
 * Banner — two kinds only (§3/§5):
 *
 *   'note'  — light risk. Say what it is and that nothing is required yet.
 *   'error' — something broke. Say what happened, when, and what is still
 *             safe, then the action. The mockup's down-site banner ends with
 *             "Nightly backup from 02:15 is intact" for exactly that reason.
 *
 * Square by design: the 4px left accent bar and a rounded corner together read
 * as a rendering bug (§5, "no rounded corners on single-sided borders").
 *
 * `title` is required — a banner without one would leave colour carrying the
 * meaning on its own (§8).
 */

const KINDS = {
  note: {
    className: 'bg-warn-bg border-l-warn',
    titleClassName: 'text-[#8B5206]',
  },
  error: {
    className: 'bg-err-bg border-l-err',
    titleClassName: 'text-err',
  },
  // 'success' is the third kind §3 implies but §5 didn't list: "healthy /
  // succeeded → --color-ok". The ops panels genuinely need it — a power action
  // or a cache cleanup that worked has to say so, and reporting it as a `note`
  // (amber) would read as a warning.
  success: {
    className: 'bg-s-green border-l-ok',
    titleClassName: 'text-[#046B57]',
  },
};

/** `compact` drops the padding and type size for a notice inside a panel rather
 *  than at the top of a page. Same colours, same square left bar. */
export function Banner({ kind = 'note', title, children, action, compact = false, className = '' }) {
  const spec = KINDS[kind] ?? KINDS.note;
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={[
        'flex items-start gap-3 border-l-4 leading-[1.55]',
        compact ? 'px-3 py-2 text-[12.5px]' : 'px-4 py-[13px] text-[13.5px]',
        spec.className,
        className,
      ].join(' ')}
    >
      <div className="flex-1 min-w-0">
        <b className={['block font-semibold', compact ? '' : 'mb-0.5', spec.titleClassName].join(' ')}>
          {title}
        </b>
        {children && <span className="text-t2">{children}</span>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
